// Server-backed replacement for the demo's localStorage.
//
// The screen code (src/app) reads and writes its data under the same names as in the demo
// (qv-plat, qv-cfg, qv-cfg-<project>, qv-ucalls-<project>). Those names are served from
// the start-up data and every change is sent to the server as a small diff:
//   documents: the top-level parts that changed (log arrays: only the new entries),
//   calls:     the calls that changed or were removed.
// Everything else (language, theme, collapsed menu, ...) stays in the browser's localStorage.
import { api } from './api';

type Json = any;

const isDoc = (k: string) => k === 'qv-plat' || k === 'qv-cfg' || (k.startsWith('qv-cfg-') && k !== 'qv-cfg-t') || k.startsWith('qv-layout-');
const isCalls = (k: string) => k.startsWith('qv-ucalls-');
export const isSynced = (k: string) => isDoc(k) || isCalls(k);

const LOG_KEYS = ['audit', 'accLog'];
const DEBOUNCE_MS = 300;

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);
/** Log entries in `cur` that were not in `prev` (logs only grow at the front). */
function added(prev: Json[] | undefined, cur: Json[] | undefined): Json[] {
  const seen = new Set((Array.isArray(prev) ? prev : []).map(x => JSON.stringify(x)));
  return (Array.isArray(cur) ? cur : []).filter(x => !seen.has(JSON.stringify(x)));
}

export class ServerStore {
  private text = new Map<string, string>(); // what the screens read (JSON text)
  private sent = new Map<string, Json>(); // last state confirmed by / sent to the server
  private timers = new Map<string, number>();
  private chain: Promise<void> = Promise.resolve();
  private session: { me: Json | null };

  constructor(data: Record<string, Json>, me: Json | null) {
    this.session = { me };
    for (const [k, v] of Object.entries(data)) {
      this.text.set(k, JSON.stringify(v));
      this.sent.set(k, v);
    }
  }

  getItem(k: string): string | null {
    if (k === 'qv-sess2') return this.session.me ? '1' : null;
    if (k === 'qv-me') return this.session.me ? JSON.stringify(this.session.me) : null;
    if (isSynced(k)) return this.text.get(k) ?? null;
    try { return localStorage.getItem(k); } catch { return null; }
  }

  setItem(k: string, v: string): void {
    if (k === 'qv-sess2' || k === 'qv-me') return; // the server decides who is signed in
    if (!isSynced(k)) { try { localStorage.setItem(k, v); } catch { /* private mode */ } return; }
    if (!this.session.me) return; // nothing is saved before sign-in
    if (this.text.get(k) === v) return;
    this.text.set(k, v);
    clearTimeout(this.timers.get(k));
    this.timers.set(k, window.setTimeout(() => this.push(k), DEBOUNCE_MS));
  }

  removeItem(k: string): void {
    if (isSynced(k) || k === 'qv-sess2' || k === 'qv-me') return;
    try { localStorage.removeItem(k); } catch { /* ignore */ }
  }

  /** Waits until every pending change has reached the server. */
  flush(keepalive = false): Promise<void> {
    for (const [k, t] of this.timers) { clearTimeout(t); this.timers.delete(k); this.push(k, keepalive); }
    return this.chain;
  }

  private push(k: string, keepalive = false): void {
    this.timers.delete(k);
    this.chain = this.chain.then(() => this.send(k, keepalive)).catch(e => console.error('[store]', k, e));
  }

  private async send(k: string, keepalive: boolean): Promise<void> {
    const raw = this.text.get(k);
    if (raw == null) return;
    const cur = JSON.parse(raw);
    const prev = this.sent.get(k);
    const body = isCalls(k) ? diffCalls(prev, cur) : diffDoc(k, prev, cur);
    if (!body) return;
    const r = await api(`/api/store/${encodeURIComponent(k)}`, { method: 'PATCH', body: JSON.stringify(body), keepalive });
    if (r.status === 401) { location.reload(); return; }
    if (!r.ok) throw new Error('HTTP ' + r.status);
    const res = await r.json();
    if (res.ignored?.length) console.warn('[store] not permitted, not saved:', k, res.ignored);
    this.sent.set(k, cur);
  }
}

function diffDoc(k: string, prev: Json, cur: Json): Json | null {
  prev = prev || {};
  const set: Json = {}, del: string[] = [];
  for (const key of Object.keys(cur || {})) {
    if (same(prev[key], cur[key])) continue;
    if (LOG_KEYS.includes(key)) { const n = added(prev[key], cur[key]); if (n.length) set[key] = n; continue; }
    if (k === 'qv-plat' && key === 'sec') {
      const p = prev.sec || {}, c = { ...(cur.sec || {}) };
      c.log = added(p.log, c.log);
      const onlyLog = Object.keys({ ...p, ...cur.sec }).every(x => x === 'log' || same(p[x], cur.sec[x]));
      if (onlyLog) { if (c.log.length) set.sec = { log: c.log }; continue; }
      set.sec = c; continue;
    }
    set[key] = cur[key];
  }
  for (const key of Object.keys(prev)) if (!(key in (cur || {}))) del.push(key);
  return Object.keys(set).length || del.length ? { set, del } : null;
}

function diffCalls(prev: Json[] | undefined, cur: Json[]): Json | null {
  const before = new Map((prev || []).map(c => [c.id, c]));
  const upsert = (cur || []).filter(c => !same(before.get(c.id), c));
  const ids = new Set((cur || []).map(c => c.id));
  const del = [...before.keys()].filter(id => !ids.has(id));
  return upsert.length || del.length ? { upsert, del } : null;
}
